<template>
  <div class="form-container">
    <h1 class="mb-4 text-center"><span class="me-4">* *</span>業者別作業予定表<span class="ms-4">* *</span></h1>

    <form @submit.prevent="updateMonthlySchedule" @keydown.enter.prevent>

      <div class="header mb-3">
        <div class="middle-title d-flex justify-content-between align-items-center">
          <div class="one-third-block">
            <p class="mb-1">株式会社創英企画 御中</p>
            <p class="mb-0">FAX 03-3370-8157</p>
          </div>
          <div class="one-third-block">
            <p class="mb-0 text-center">作業予定日 {{ workPeriod }}</p>
          </div>
          <div class="one-third-block">
            <p class="mb-1 text-center">{{ vendor.vendor_nm}}</p>
            <p class="mb-1 text-center"><input v-model="vendor_work_schedule.destination_address_01" id="work_content" type="text" placeholder="送付先宛名" class="form-control w-half"></p>
            <p class="mb-0 text-center">FAX {{ vendor.fax}}</p>
          </div>
        </div>
      </div>
      
      <!-- 業務内容のテーブル -->
      <table class="table monthly-detail-data">
        <thead>
          <tr>
            <th class="month-data-header align-middle text-start">作業項目</th>
            <th class="month-data-header align-middle text-start">ビル名</th>
            <th class="month-data-header align-middle">作業予定日<br><span class="text-danger">請求書の日付に反映されます</span></th>
            <th class="month-data-header align-middle">作業開始時間</th>
            <th class="month-data-header align-middle">作業終了時間</th>
            <th class="month-data-header align-middle">年度</th>
            <th class="month-data-header align-middle text-start" colspan="2">発注金額</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(schedule, scIndex) in vendor_work_schedule.t_vendor_work_monthly_calendar_details" :key="scIndex">
            <td class="text-start">{{ schedule?.work_content }}</td>
            <td class="text-start">{{ schedule?.building_nm }}</td>
            <!-- 作業予定日入力フィールド -->
            <td>
              <input
                v-model="schedule.work_plan_dt"
                type="date"
                class="form-control"
                :min="nextMonthStart"
                :max="nextMonthEnd"
              >
            </td>
            <!-- 作業開始時間 -->
            <td>
              <input 
              :value="formatTime(schedule.start_work_plan_time)" 
              @input="event => updateStartTime(event.target.value, scIndex)" 
              type="time" 
              class="form-control">
            </td>
            <!-- 作業終了時間 -->
            <td>
              <input 
              :value="formatTime(schedule.end_work_plan_time)" 
              @input="event => updateEndTime(event.target.value, scIndex)" 
              type="time" 
              class="form-control">
            </td>
            <td>{{ schedule.work_year }}</td>
            <td class="text-start">{{ formatCurrency(schedule?.request_amount) }}</td>
            <td></td>
          </tr>
          <tr>
            <td class="text-start" colspan="6"><span class="me-4">* *</span>業者合計<span class="ms-4">* *</span></td>
            <td class="text-start">{{ calculateTotal(vendor_work_schedule.t_vendor_work_monthly_calendar_details) }}</td>
            <td class="text-nowrap">{{ vendor_work_schedule.t_vendor_work_monthly_calendar_details?.length }}件</td>
          </tr>
        </tbody>
      </table>

      <!-- ボタンエリア -->
      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-warning mx-1">予定表更新</button>
      </div>

    </form>
  </div>
</template>

<script>
import { ref, computed, onMounted, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import { updateVendorMonthlyWorkSchedule, getVendor, getVendorMonthlyWorkSchedule } from '@/api/axios';

export default {
  setup() {
    const title = ref('予定表編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const vendor = ref({});
    const schedules = ref([]);
    const errorMessages = ref({});

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const scheduleId = route.params.id
    const vendorId = route.params.vendor_id;
    const year = route.params.year;
    const month = route.params.month;
    const today = new Date().toISOString().split('T')[0]; // "YYYY-MM-DD"形式

    const vendor_work_schedule = ref({
      plan_year: year,
      plan_month: month,
      m_vendor_id: vendorId,
      destination_address_01: '',
      publication_datetime: '',
      t_vendor_work_monthly_calendar_details: []
    });

    // 月の最初の日と最後の日を取得する関数
    const getFirstAndLastDay = () => {
      const firstDay = new Date(year, month - 1, 1); // 月の初日
      const lastDay = new Date(year, month, 0); // 月の末日

      const formatDate = (date) => {
        return date.toLocaleDateString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).replace(/\//g, '/');
      };

      return {
        firstDay: formatDate(firstDay),
        lastDay: formatDate(lastDay)
      };
    };

    // 初日と末日を計算して取得
    const workPeriod = computed(() => {
      const { firstDay, lastDay } = getFirstAndLastDay();
      return `${firstDay} ~ ${lastDay}`;
    });

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');
      
      try {
        const response = await getVendor(vendorId);
        vendor.value = response.data;
      } catch (error) {
        console.error('建物情報データの取得に失敗しました:', error);
      }
      
      try {
        const response = await getVendorMonthlyWorkSchedule(scheduleId, vendorId, year, month);
        vendor_work_schedule.value = response.data.vendor_work_schedule
        schedules.value = response.data.vendor_monthly_works;

        // console.log(schedules.value)

        vendor_work_schedule.value.t_vendor_work_monthly_calendar_details = schedules.value.t_vendor_work_monthly_calendar_details.map((schedule) => {
          return {
            work_content: schedule?.t_building_work_schedule_detail?.t_building_work_annual_schedule?.work_content,
            building_nm: schedule?.t_building_work_schedule_detail?.t_building_work_annual_schedule?.m_building.building_nm,
            t_vendor_work_monthly_calendar_id: schedule?.t_building_work_schedule_detail?.t_building_work_annual_schedule?.id, // IDを対応させる
            t_building_work_schedule_detail_id: schedule?.t_building_work_schedule_detail?.id,
            work_plan_dt: schedule?.work_plan_dt, // 必要に応じて設定
            start_work_plan_time: schedule?.start_work_plan_time,
            end_work_plan_time: schedule?.end_work_plan_time,
            request_amount: schedule?.t_building_work_schedule_detail?.request_amount,
            sort_no: schedule?.t_vendor_work_monthly_calendar_detail?.sort_no
          };
        });

      } catch (error) {
        console.error('請負業者予定表の取得に失敗しました:', error);
      }
    };

    // Format currency
    const formatCurrency = (value) => {
      if (value) {
        return new Intl.NumberFormat('ja-JP', {
          style: 'currency',
          currency: 'JPY',
        }).format(value);
      }
      return '¥0';
    };

    // 合計を計算するメソッド
    const calculateTotal = (details) => {
      if (!details) return '¥0'; // 詳細がない場合は¥0を返す
      const total = details.reduce((sum, detail) => {
        // detail.bill_amountがあれば加算し、なければ0を加算
        return sum + (detail.t_building_work_schedule_detail?.request_amount || 0);
      }, 0);
      return formatCurrency(total); // フォーマットされた通貨として返す
    };

    // ISO 8601形式の日時からHH:mm形式への変換関数
    const formatTime = (dateTimeString) => {
      if (!dateTimeString) return null;
      return dateTimeString.substring(11, 16); // 直接文字列から時間部分を抽出
    };

    // 開始時間更新関数
    const updateStartTime = (newTime, scIndex) => {
      if (!newTime) {
        vendor_work_schedule.value.t_vendor_work_monthly_calendar_details[scIndex].start_work_plan_time = null;
      }else{
        const newDateTimeString = `2024-01-01T${newTime}:00.000+09:00`; // 日付は固定または適宜調整
        vendor_work_schedule.value.t_vendor_work_monthly_calendar_details[scIndex].start_work_plan_time = newDateTimeString;
      }
    };

    // 終了時間更新関数
    const updateEndTime = (newTime, scIndex) => {
      if (!newTime) {
        vendor_work_schedule.value.t_vendor_work_monthly_calendar_details[scIndex].end_work_plan_time = null;
      }else{
        const newDateTimeString = `2024-01-01T${newTime}:00.000+09:00`; // 日付は固定または適宜調整
        vendor_work_schedule.value.t_vendor_work_monthly_calendar_details[scIndex].end_work_plan_time = newDateTimeString;
      }
    };

    const updateMonthlySchedule = async () => {
      try {
        await updateVendorMonthlyWorkSchedule(scheduleId, vendor_work_schedule.value);
        store.dispatch('setFlashMessage', { message: '請負業者予定表更新完了！', type: 'success' });
        router.push({
          path: `/vendor-work-monthly-calendar-detail/${vendorId}/${year}/${month}`,
          query: { ...route.query, year, month }
        });
      } catch (error) {
        console.error('作成中にエラーが発生しました:', error);
      }
    };

    // ローカル日付フォーマット関数
    const formatDate = (date) => {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    };

    // 翌月の範囲
    const nextMonthStart = computed(() => {
      return formatDate(new Date(year, month - 1, 1));
    });

    const nextMonthEnd = computed(() => {
      return formatDate(new Date(year, month, 0));
    });

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      title,
      month,
      today,
      vendor,
      schedules,
      workPeriod,
      formatTime,
      updateStartTime,
      updateEndTime,
      formatCurrency,
      calculateTotal,
      errorMessages,
      vendor_work_schedule,
      updateMonthlySchedule,
      nextMonthStart,
      nextMonthEnd
    };
  }
};
</script>

<style scoped>
.form-container {
  padding: 20px;
  width: 100%;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* ヘッダ */
.header h2 {
  font-weight: bold;
}

.header p {
  text-align: center;
  margin-bottom: 20px;
}

.text-center {
  text-align: center;
}

/* テーブルのスタイル */
table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  padding: 8px;
  border-bottom: 1px solid #000;
  text-align: center;
}

tr:last-child td{
  border-bottom: none;
}

.small-input {
  width: 50px;
}

.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

/* 特記事項のスタイル */
.special-notes-table {
  width: 100%;
  margin-top: 20px;
  border-collapse: collapse;
}

.special-notes-table th, .special-notes-table td {
  border: 1px solid #000;
}

.no-resize {
  resize: none;
}

/* ボタンエリア */
.btn-area {
  text-align: center;
  margin-top: 20px;
}
</style>
